import React, {useState} from 'react';
import {TicketType, TicketTypeData} from "types/TicketType";
import {Form, Formik, FormikHelpers, FormikProps} from "formik";
import Field from "components/Form/Field";
import FormControls from "components/FormControls";
import FieldHeader from "components/Form/FieldHeader";
import {Tab} from "@headlessui/react";
import TabButton from "components/TabButton";
import MoneyInput from "components/Form/MoneyInput";
import Select, {Option} from "components/Form/Select/Select";
import {OrganisationPreview} from "types/Organisation";
import IconName from "types/IconName";
import ngage from "services/ngage";
import * as Yup from "yup";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";
import {TextAreaField} from "../../../components/Form/Form";
import {useOperator} from "../../../context/OperatorContext";

enum PriceMode {FREE, CHARGE}
enum OrganisationMode {ALL, SELECTED}

type Props = {
    ticketType?: TicketType
    onSubmit: (values: TicketTypeData, helpers: FormikHelpers<TicketTypeData>) => any
}

const organisationOption = (organisation: OrganisationPreview): Option => ({
    id: organisation.id,
    name: organisation.name,
    icon: IconName.GLOBE_ALT
})

function TicketTypeForm(props: Props) {
    const {termFor} = useOperator();
    const [priceMode, setPriceMode] = useState<PriceMode>(props.ticketType?.price? PriceMode.CHARGE : PriceMode.FREE);

    const [organisationMode, setOrganisationMode] = useState<OrganisationMode>((!!props.ticketType?.organisations?.length)? OrganisationMode.SELECTED : OrganisationMode.ALL);
    const [selectedOrganisations, setSelectedOrganisations] = useState<Array<Option>>(props.ticketType?.organisations?.map(organisationOption) || []);

    const selectPriceMode = (mode: PriceMode, {setFieldValue}: FormikHelpers<TicketTypeData>) => {
        void setFieldValue('price', mode === PriceMode.FREE? 0 : 1000)
        setPriceMode(mode)
    }

    function selectOrganisationMode(mode: OrganisationMode, {setFieldValue}: FormikHelpers<TicketTypeData>) {
        void setFieldValue('organisation_ids', [])
        setSelectedOrganisations([])
        setOrganisationMode(mode)
    }

    const organisationSource = async (query: string) => {
        return (await ngage.organisations.filtered({search: query || null}))?.data.map(organisationOption) || []
    }

    const handleSelectOrganisations = (organisations: Array<Option>, formik: FormikProps<TicketTypeData>) => {
        void formik.setFieldValue('organisation_ids', organisations.map(organisation => organisation.id))
        setSelectedOrganisations(organisations)
    }

    const validationSchema = Yup.object().shape({
        name: Yup.string().required("This field is required").max(255, 'Maximum of 255 characters'),
        description: Yup.string().required("This field is required").max(255, 'Maximum of 255 characters'),
        price: Yup.number().required("This field is required"),
    })

    const initialValues: TicketTypeData = {
        name: props.ticketType?.name || '',
        description: props.ticketType?.description || '',
        price: props.ticketType?.price || 0,
        organisation_ids: props.ticketType?.organisations?.map(org => org.id) || []
    }

    return (
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={props.onSubmit}>
            {formik => {
                return (
                    <Form className="space-y-8 divide-y divide-gray-200">
                        <ErrorNotification formik={formik} />

                        <div className="shadow sm:rounded-md">
                            <div className="px-4 py-5 bg-white sm:p-6 sm:rounded-md">
                                <div className="space-y-8 divide-y divide-gray-200">
                                    <div>
                                        <div>
                                            <h3 className="text-lg leading-6 font-medium text-gray-900">
                                                Key Information
                                            </h3>
                                            <p className="mt-1 text-sm text-gray-500">
                                                Basic details about the {termFor('ticket type')}
                                            </p>
                                        </div>
                                        <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">

                                            {/* Name */}
                                            <div className="sm:col-span-3">
                                                <div className="mt-1">
                                                    <Field
                                                        label='Name *'
                                                        hint='255 chars max'
                                                        type="text"
                                                        name="name"
                                                        id="name"
                                                        autoComplete="off"
                                                        maxLength="255"
                                                        autoFocus
                                                    />
                                                </div>
                                            </div>

                                            {/* Description */}
                                            <div className="sm:col-span-5">
                                                <div className="mt-1">
                                                    <TextAreaField
                                                        label='Description *'
                                                        hint="255 chars max"
                                                        name="description"
                                                        id="description"
                                                        autoComplete="off"
                                                        maxChars={255}
                                                        rows={3}
                                                    />
                                                </div>
                                            </div>

                                            {/* Price */}
                                            <div className="sm:col-span-3 sm:col-start-1 md:col-span-2 md:col-start-1 xl:col-span-1 xl:col-start-1">
                                                <FieldHeader label="Price *" />
                                                <Tab.Group selectedIndex={priceMode} onChange={index => selectPriceMode(index, formik)}>
                                                    <Tab.List className="mt-1 mb-1 relative z-0 inline-flex shadow-sm rounded-md">
                                                        <TabButton position={TabButton.positions.FIRST}>Free</TabButton>
                                                        <TabButton position={TabButton.positions.LAST}>Charge</TabButton>
                                                    </Tab.List>
                                                    <Tab.Panels>
                                                        <Tab.Panel/>
                                                        <Tab.Panel>
                                                            <MoneyInput
                                                                currency={MoneyInput.Currency.GBP}
                                                                value={formik.values.price}
                                                                onChange={amount => formik.setFieldValue('price', amount)}
                                                            />
                                                        </Tab.Panel>
                                                    </Tab.Panels>
                                                </Tab.Group>
                                            </div>

                                        </div>
                                    </div>

                                    <div className="pt-8">
                                        <div>
                                            <h3 className="text-lg leading-6 font-medium text-gray-900">
                                                Organisations
                                            </h3>
                                            <p className="mt-1 text-sm text-gray-500">
                                                Organisations to which this {termFor('ticket type')} relates
                                            </p>
                                        </div>
                                        <div className="mt-6">

                                            <Tab.Group selectedIndex={organisationMode} onChange={index => selectOrganisationMode(index, formik)}>
                                                <Tab.List className="mt-1 mb-1 relative z-0 inline-flex shadow-sm rounded-md">
                                                    <TabButton position={TabButton.positions.FIRST}>All</TabButton>
                                                    <TabButton position={TabButton.positions.LAST}>Selected</TabButton>
                                                </Tab.List>
                                                <Tab.Panels>
                                                    <Tab.Panel/>
                                                    <Tab.Panel className="mt-2 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                                                        {/* Organisations */}
                                                        <div className="sm:col-span-6">
                                                            <Select
                                                                label="Organisations"
                                                                hint="Type to search"
                                                                source={organisationSource}
                                                                selected={selectedOrganisations}
                                                                onChange={selected => handleSelectOrganisations(selected, formik)}
                                                                multiple
                                                            />
                                                        </div>
                                                    </Tab.Panel>
                                                </Tab.Panels>
                                            </Tab.Group>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <FormControls formik={formik} />
                        </div>
                    </Form>
                )
            }}
        </Formik>
    );
}

export default TicketTypeForm;